import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import calculateCCAfterFee, { calculateFeePercentage } from '../../../../../utils/stripe-payment-fee.util';
import { SQUARE_INITIATE_PAYMENT } from '../../../../../constants/square-endpoints.constant';

// Moved outside component — these are static style objects and don't need to
// be recreated on every render.
const buttonStyle = {
  backgroundColor: '#000',
  border: 'none',
  borderRadius: '6px',
  height: '44px',
  minWidth: '180px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  cursor: 'pointer',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
};

const svgStyle = {
  height: '18px',
  width: 'auto',
  fill: '#fff',
  display: 'block',
};

function SquareGoogleApplePay(props) {
  const { register, control } = useFormContext();

  const selectedAmount = useWatch({ control, name: 'selected_amount' });
  const customAmount = useWatch({ control, name: 'custom_amount' });
  const coverProcessingFee = useWatch({ control, name: 'cover_processing_fee' });

  const [errorMessage, setErrorMessage] = useState('');
  const [isWalletReady, setIsWalletReady] = useState(false);
  // FIX 1: Track wallet type as state so the render reacts when it changes.
  // Previously this was only stored in a ref, which doesn't trigger re-renders,
  // making the Google Pay <div> visibility and Apple Pay button conditional
  // both unreliable.
  const [walletType, setWalletType] = useState('');

  const walletRef = useRef(null);
  const paymentRequestRef = useRef(null);
  // Keep the ref in sync with the state so non-render code (handlePayment)
  // can still read the current value synchronously.
  const walletTypeRef = useRef('');

  const parentLoading = useMemo(() => {
    return props?.loading;
  });

  const countryCode = useMemo(() => {
    const country = props?.campaignDetail?.country;
    return country?.toLowerCase() === 'usa' ? 'US' : country;
  }, [props?.campaignDetail?.country]);

  const currencyCode = useMemo(() => {
    return props?.campaignDetail?.currency?.toUpperCase();
  }, [props?.campaignDetail?.currency]);

  const baseAmount = useMemo(() => {
    if (props?.campaignDetail?.type === 'product') {
      return Number(props?.amount || 0);
    }

    const amountValue =
      !selectedAmount || selectedAmount === 'custom_amount'
        ? customAmount
        : selectedAmount;

    return Number(amountValue || 0);
  }, [props?.campaignDetail?.type, props?.amount, selectedAmount, customAmount]);

  const shouldCoverFee = useMemo(() => {
    return coverProcessingFee ?? props?.campaignDetail?.donation_amount?.ask_donors_cover_fee_default;
  }, [coverProcessingFee, props?.campaignDetail?.donation_amount?.ask_donors_cover_fee_default]);

  const getFinalAmount = useCallback(
    (amount) => {
      const numericAmount = Number(amount || 0);
      if (Number.isNaN(numericAmount)) return '0';

      const finalAmount = shouldCoverFee
        ? Number(calculateCCAfterFee(numericAmount))
        : numericAmount;

      return (Math.round(finalAmount * 100) / 100).toFixed(2);
    },
    [shouldCoverFee]
  );

  const updatePaymentRequest = useCallback(
    async (amount) => {
      const paymentRequest = paymentRequestRef.current;
      if (!paymentRequest) return;

      await paymentRequest.update({
        country: countryCode,
        currency: currencyCode,
        total: {
          label: 'Total',
          amount: getFinalAmount(amount),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });
    },
    [countryCode, currencyCode, getFinalAmount]
  );

  const handlePayment = useCallback(async () => {
    const wallet = walletRef.current;
    if (!wallet) return;

    // FIX 3: Guard against concurrent taps / clicks.
    setErrorMessage('');
    props?.setLoading(true);

    try {
      // For both Google Pay and Apple Pay the Square SDK exposes the same
      // tokenize() API. Calling it on an Apple Pay wallet instance opens the
      // native Apple Pay sheet; the promise resolves once the user authorises
      // or cancels.
      const tokenResult = await wallet.tokenize();

      if (tokenResult.status !== 'OK') {
        const errorText = tokenResult.errors
          ? ` and errors: ${JSON.stringify(tokenResult.errors)}`
          : '';
        setErrorMessage(`Tokenization failed with status: ${tokenResult.status}${errorText}`);
        return;
      }

      const donorInfo = {
        payment_method_id: tokenResult.token,
        donation_type: props?.selectedDonationType,
        payment_type: walletTypeRef.current,
      };

      const donationPayload = props?.returnDonationPayload?.(donorInfo);
      if (!donationPayload) {
        setErrorMessage('Unable to build donation payload.');
        return;
      }

      donationPayload.idempotent_key = uuidv4();
      donationPayload.customer_card_nonce = tokenResult.token;

      const response = await axios.post(SQUARE_INITIATE_PAYMENT, donationPayload);

      if (response?.data?.status === false) {
        setErrorMessage(response?.data?.message || 'Payment failed.');
        return;
      }

      props?.onPaymentSuccess?.('Payment successful thank you for your kind donations.');
    } catch (error) {
      setErrorMessage(error?.message || 'Something went wrong during payment.');
    } finally {
      props?.setLoading(false);
    }
  }, [props?.onPaymentSuccess, props?.returnDonationPayload, props?.selectedDonationType]);

  const createPaymentRequest = useCallback(() => {
    return props?.squareInstance?.paymentRequest({
      countryCode,
      currencyCode,
      total: {
        amount: '1',
        label: 'Total',
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });
  }, [props?.squareInstance, countryCode, currencyCode]);

  // Helper to keep both the ref and the state in sync so that:
  //   - non-render code (handlePayment) can read the value synchronously via ref
  //   - the render tree reacts to the change via state
  const setWalletTypeSync = useCallback((type) => {
    walletTypeRef.current = type;
    setWalletType(type);
  }, []);

  const initializeGooglePay = useCallback(async () => {
    const paymentRequest = createPaymentRequest();
    if (!paymentRequest) throw new Error('Unable to create payment request.');

    const googlePay = await props?.squareInstance?.googlePay(paymentRequest);
    await googlePay.attach('#square-wallet-button', { buttonSizeMode: 'fill' });

    walletRef.current = googlePay;
    paymentRequestRef.current = paymentRequest;
    setWalletTypeSync('Google Pay');
    setIsWalletReady(true);
  }, [createPaymentRequest, props?.squareInstance, setWalletTypeSync]);

  const initializeApplePay = useCallback(async () => {
    const paymentRequest = createPaymentRequest();
    if (!paymentRequest) throw new Error('Unable to create payment request.');

    const applePay = await props?.squareInstance?.applePay(paymentRequest);
    // Note: Apple Pay does NOT use .attach() — the native payment sheet is
    // triggered by calling applePay.tokenize() directly (done in handlePayment).

    walletRef.current = applePay;
    paymentRequestRef.current = paymentRequest;
    setWalletTypeSync('Apple Pay');
    setIsWalletReady(true);
  }, [createPaymentRequest, props?.squareInstance, setWalletTypeSync]);

  // Initialise whichever wallet is available in this browser / device.
  useEffect(() => {
    if (!props?.squareInstance || !props?.campaignDetail?.currency || !props?.campaignDetail?.country) {
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const canUseApplePay =
          typeof window !== 'undefined' &&
          window?.ApplePaySession &&
          window?.ApplePaySession?.canMakePayments?.();

        if (canUseApplePay) {
          await initializeApplePay();
        } else {
          await initializeGooglePay();
        }
      } catch (error) {
        if (cancelled) return;

        try {
          await initializeGooglePay();
        } catch (fallbackError) {
          setErrorMessage(fallbackError?.message || error?.message || 'Failed to initialize payment.');
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      walletRef.current = null;
      paymentRequestRef.current = null;
      walletTypeRef.current = '';
      setWalletType('');
      setIsWalletReady(false);
    };
  }, [
    props?.squareInstance,
    props?.campaignDetail?.currency,
    props?.campaignDetail?.country,
    initializeApplePay,
    initializeGooglePay,
  ]);

  // FIX 4: Notify the parent and wire up the Google Pay DOM button once the
  // wallet is ready.
  //
  // Previously, setStripeButtonElementLoader / setPaymentFlow were called
  // inside an `if (!button) return` guard, which meant they were NEVER called
  // for Apple Pay (there is no #square-wallet-button for Apple Pay).
  // Now we call them unconditionally for both wallet types, and only attach
  // the DOM click listener for Google Pay.
  useEffect(() => {
    if (!isWalletReady) return;

    // Notify parent — applies to both Google Pay and Apple Pay.
    props?.setStripeButtonElementLoader?.(false);
    // props?.setPaymentFlow?.(true);

    if (walletType !== 'Google Pay') return;

    // Google Pay: the Square SDK renders its own button inside the div;
    // we wire up our payment handler to that rendered button.
    const button = document.getElementById('square-wallet-button');
    if (!button) return;

    button.addEventListener('click', handlePayment);
    return () => {
      button.removeEventListener('click', handlePayment);
    };
  }, [isWalletReady, walletType, handlePayment, props]);

  useEffect(() => {
    if (!paymentRequestRef.current) return;
    updatePaymentRequest(baseAmount);
  }, [baseAmount, updatePaymentRequest]);

  useEffect(() => {
    if (props?.formStep !== 'payment') return;
    updatePaymentRequest(baseAmount);
  }, [props?.formStep, baseAmount, updatePaymentRequest]);

  const handleCoverFeeChange = useCallback(
    (e) => {
      props?.calculateStripeFee?.(e);
      updatePaymentRequest(baseAmount);
    },
    [props, baseAmount, updatePaymentRequest]
  );

  const isApplePay = isWalletReady && walletType === 'Apple Pay';

  return (
    <>
      {/* <p className="text-center f-18 mb-3">Choose a payment option:</p> */}

      {/* {props?.campaignDetail?.donation_amount?.ask_donors_cover_fee &&
      props?.formStep === 'request_button' ? (
        <div className="col-lg-12 mt-2 mb-2">
          <div className="d-flex align-items-center justify-content-start">
            <input
              defaultChecked={Boolean(props?.campaignDetail?.donation_amount?.ask_donors_cover_fee_default)}
              {...register('cover_processing_fee', {
                onChange: handleCoverFeeChange,
              })}
              className="me-1"
              type="checkbox"
            />
            <label className="f-14">
              Optionally add ${calculateFeePercentage(props?.amount)} to cover processing fees
            </label>
          </div>
        </div>
      ) : null} */}

      {/* Google Pay: Square SDK renders its own button inside this div.
          Hidden when Apple Pay is active; shown via state-driven walletType. */}
      <div
        id="square-wallet-button"
        style={{ display: walletType === 'Google Pay' ? 'block' : 'none' }}
      />

      {/* Apple Pay: custom button that triggers the native payment sheet via
          wallet.tokenize(). Disabled while a payment request is in-flight. */}
      {isApplePay && (
        <button
          id="apple-pay-button"
          type="button"
          style={{
            ...buttonStyle,
            opacity: parentLoading ? 0.6 : 1,
            cursor: parentLoading ? 'not-allowed' : 'pointer',
          }}
          onClick={handlePayment}
          disabled={parentLoading}
          aria-label="Pay with Apple Pay"
        >
          <svg viewBox="0 0 30 43" style={svgStyle} xmlns="http://www.w3.org/2000/svg">
            <g fill="white">
              <path d="M19.4 5.56c1.2-1.5 2.01-3.5 1.8-5.56-1.75.08-3.89 1.15-5.13 2.65-1.11 1.28-2.09 3.38-1.83 5.35 1.97.17 3.94-.98 5.16-2.44z" />
              <path d="M21.17 8.39c-2.86-.17-5.29 1.62-6.65 1.62s-3.45-1.54-5.71-1.5C5.86 8.56 3.13 10.23 1.63 12.87-1.44 18.17.82 26.02 3.81 30.33c1.45 2.13 3.2 4.48 5.5 4.4 2.18-.08 3.03-1.41 5.68-1.41 2.64 0 3.41 1.41 5.71 1.37 2.39-.04 3.88-2.13 5.33-4.26 1.66-2.43 2.35-4.77 2.39-4.89-.04-.04-4.61-1.79-4.65-7.04-.04-4.39 3.59-6.48 3.76-6.61-2.05-3.03-5.25-3.37-6.36-3.46z" />
            </g>
          </svg>
          <span className="f-22">{parentLoading ? 'Processing…' : 'Pay'}</span>
        </button>
      )}

      {errorMessage ? <p className="text-danger">{errorMessage}</p> : null}

      {/* <button
        onClick={() => props?.setPaymentFlow?.('info')}
        style={{
          backgroundColor: props?.campaignDetail?.primary_color || '#005B97',
          borderBottom: props?.campaignDetail?.primary_color
            ? `2px solid ${props.campaignDetail.primary_color}`
            : '',
        }}
        type="button"
        className="btn btn-md w-100 text-white btn-step-one mt-4"
      >
        <span>
          <span className="f-18 mx-4">
            <i className="fa-regular fa-credit-card f-18" /> Credit / Debit Card{' '}
            {props?.campaignDetail?.enable_ach ? ' / ACH' : ''}
          </span>
        </span>
      </button> */}
    </>
  );
}

export default SquareGoogleApplePay;